import { useState } from 'react';
import type { NavigationQuestionDTO } from '../models/StudentExamViewDTOs';
import { translate } from '@/src/utils';

import TimeRemaining from './TimeRemaining';

type AssessmentNavigationMatrixProps = {
    questions: NavigationQuestionDTO[];
    loading: boolean;
    language: 'en' | 'es';
    title?: string;
    activeSegmentNumber: number;
    onButtonClick: (questionRoute: string, replace: boolean) => void;
    selectedQuestionId?: number;
};

export default function AssessmentNavigationMatrix({
    questions,
    loading,
    language,
    title,
    activeSegmentNumber,
    onButtonClick,
    selectedQuestionId = 0,
}: AssessmentNavigationMatrixProps) {
    // Group questions by segmentNumber
    const grouped = questions.reduce<Record<number, NavigationQuestionDTO[]>>((acc, q) => {
        if (!acc[q.segmentNumber]) acc[q.segmentNumber] = [];
        acc[q.segmentNumber].push(q);
        return acc;
    }, {});

    const nSegments = Object.keys(grouped).length;
    const isLastSegmentActive = activeSegmentNumber === nSegments;
    const [showOnMobile, setShowOnMobile] = useState<boolean>(false);

    return (
        <div className="assessment-nav-matrix">
            <TimeRemaining />

            <div className="mb-2 lg:hidden">
                <button
                    type="button"
                    aria-expanded={showOnMobile}
                    aria-controls="assessment-nav-content"
                    onClick={() => setShowOnMobile((s) => !s)}
                    className="assessment-nav-btn blue"
                >
                    {showOnMobile ? 'Hide Questions' : 'Show Questions'}
                </button>
            </div>

            {title && <h3 className="assessment-title">{title}</h3>}

            <div
                id="assessment-nav-content"
                className={`${showOnMobile ? 'block' : 'hidden'} lg:block`}
            >
                {Object.entries(grouped).map(([segmentNumber, segQuestions]) => {
                    const hideReviewButton = selectedQuestionId !== 0;
                    const isInLastSegment = parseInt(segmentNumber) === nSegments;
                    const isInActiveSegment = parseInt(segmentNumber) === activeSegmentNumber;
                    return (
                        <div key={segmentNumber} className="assessment-segment">
                            {nSegments > 1 && (
                                <>
                                    <hr className="my-2" />
                                    <h3 className="assessment-segment-title">
                                        Segment {segmentNumber}
                                    </h3>
                                </>
                            )}
                            <div className="assessment-segment-row mt-3">
                                {segQuestions.map((q) => {
                                    const displayValue =
                                        language === 'en'
                                            ? q.displayValue
                                            : q.displayValue_es || q.displayValue;
                                    return (
                                        <button
                                            key={q.orderNumber}
                                            onClick={() => onButtonClick(`q/${q.id}`, true)}
                                            className={[
                                                'assessment-nav-btn blue',
                                                q.id === selectedQuestionId && 'active',
                                                q.isAnswered && 'answered',
                                                !q.isAnswered && q.isViewed && 'viewed',
                                                q.isFlagged && 'flagged',
                                                getWidthClass(displayValue),
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            title={displayValue}
                                            disabled={loading || !isInActiveSegment}
                                        >
                                            {displayValue}
                                        </button>
                                    );
                                })}
                                {/* Add "Review" to last segment */}
                                {isInLastSegment && hideReviewButton && (
                                    <button
                                        key="exam-attempt-review-button"
                                        onClick={() =>
                                            onButtonClick(`summary/${selectedQuestionId}`, false)
                                        }
                                        className={[
                                            'assessment-nav-btn',
                                            'blue',
                                            getWidthClass(translate(language, 'review')),
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        title="Review"
                                        disabled={loading || !isLastSegmentActive}
                                    >
                                        {translate(language, 'review')}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function getWidthClass(text: string) {
    const len = (text || '').length;
    if (len <= 3) return 'u-span-1';
    if (len <= 8) return 'u-span-2';
    if (len <= 13) return 'u-span-3';
    return 'u-span-4';
}
